import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useRating } from '~/_shared/components/rating/rating.hooks';
import { useOnUnmount } from '~/_shared/hooks/useOnUnmount';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import { useModal } from '~/modal/useModal.hook';
import { announcementsSetShowRatingModal } from '~/store/announcements/announcements.actionCreators';
import { useShowRatingModalSelector } from '~/store/announcements/announcements.selector';
import { useHasNavigatedToHomeSelector } from '~/store/frontendState/navigation/navigation.selectors';
import type { TopUnderbarItem } from '../item/topUnderbarItem.types';
import { renderNps } from './topUnderbarNps.component';

const NPS_DETAILS_AUTO_OPEN_THRESHOLD = 6;

type SubmitSelectedScoreOptions = Readonly<{
  comment?: string;
  keepalive?: boolean;
}>;

export const useNpsTopUnderbarItem = (): TopUnderbarItem | null => {
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [isNpsDismissed, setIsNpsDismissed] = useState(false);
  const { openModal: openNpsDetailsModal } = useModal('NpsDetails');
  const theme = useTheme();
  const { submitRating, dismissRatingModal } = useRating();
  const showRatingModal = useShowRatingModalSelector();
  const hasNavigatedHomeFromAdminMapPage = useHasNavigatedToHomeSelector();
  const dispatch = useDispatch();

  // the last selected score is only remembered and gets submitted at most once, when the user is done with the NPS
  const unsubmittedScoreRef = useRef<number | null>(null);
  const alreadySubmittedRef = useRef<boolean>(false);

  const submitSelectedScore = useCallback(({ comment, keepalive }: SubmitSelectedScoreOptions = {}) => {
    const score = unsubmittedScoreRef.current;
    if (isNullsy(score) || alreadySubmittedRef.current) {
      return;
    }

    unsubmittedScoreRef.current = null;
    alreadySubmittedRef.current = true;
    dispatch(announcementsSetShowRatingModal(false));
    submitRating({ rating: score, comment }, { keepalive })
      .catch((e) => console.error('Failed to submit rating', e));
  }, [dispatch, submitRating]);

  useEffect(() => {
    const handlePageHide = () => submitSelectedScore({ keepalive: true });

    window.addEventListener('pagehide', handlePageHide);

    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [submitSelectedScore]);

  useOnUnmount(() => {
    submitSelectedScore();
  });

  const item = useMemo(() => {
    if (isNpsDismissed || !showRatingModal || !hasNavigatedHomeFromAdminMapPage) {
      return null;
    }

    const dismissNps = () => {
      setIsNpsDismissed(true);
    };

    const handleOpenNpsDetailsModal = (score: number) => {
      openNpsDetailsModal({
        score,
        onSave: (description) => {
          submitSelectedScore({ comment: description.trim() ? description : undefined });
          dismissNps();
        },
        onClose: () => {
          submitSelectedScore();
          dismissNps();
        },
      });
    };

    return renderNps({
      isScoreSaved: notNullsy(npsScore),
      score: npsScore,
      onScoreSelect: (score) => {
        setNpsScore(score);
        unsubmittedScoreRef.current = score;

        if (score < NPS_DETAILS_AUTO_OPEN_THRESHOLD) {
          handleOpenNpsDetailsModal(score);
        }
      },
      onTellUsMore: () => {
        if (isNullsy(npsScore)) {
          return;
        }

        handleOpenNpsDetailsModal(npsScore);
      },
      onClose: () => {
        if (isNullsy(npsScore)) {
          dismissRatingModal().catch((e) => console.error('Failed to dismiss rating', e));
          dispatch(announcementsSetShowRatingModal(false));
        }
        else {
          submitSelectedScore();
        }

        dismissNps();
      },
      theme,
    });
  }, [isNpsDismissed, openNpsDetailsModal, npsScore, theme, submitSelectedScore, dismissRatingModal,
    showRatingModal, hasNavigatedHomeFromAdminMapPage, dispatch,
  ]);

  const previousItem = usePrevious(item);

  useEffect(() => {
    if (previousItem && !item) {
      // Handle the case when the item was previously rendered but is now null
      submitSelectedScore();
    }
  }, [previousItem, item, submitSelectedScore]);

  return item;
};
